import { Button } from '@/components/Button/Button';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import type {
  ReportFormData,
  RepeatableSectionConfig,
} from '@/screens/ReportCreation/reportCreation.types';
import { prefixFormSections } from '@/screens/ReportCreation/utils/repeatableSections.utils';
import { Ionicons } from '@expo/vector-icons';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Separator, SizableText, useTheme, XStack, YStack } from 'tamagui';
import { FormSectionList } from './FormSectionList';
import { FieldTitle } from '@components/FieldTitle/FieldTitle';

interface RepeatableSectionContainerProps {
  config: RepeatableSectionConfig;
}

/**
 * Handles rendering of repeatable section blocks (e.g., Diagnostic Procedures).
 * Internally relies on react-hook-form field arrays but keeps the UI driven by
 * the same `FormSection` definitions used elsewhere.
 */
export const RepeatableSectionContainer = ({
  config,
}: RepeatableSectionContainerProps): React.ReactElement => {
  const { control } = useFormContext<ReportFormData>();
  const minInstances = config.minInstances ?? 1;
  const theme = useTheme();
  const { t } = useAppTranslation();

  const { fields, append, remove } = useFieldArray<ReportFormData>({
    control,
    name: config.id,
  });

  useEffect(() => {
    if (fields.length >= minInstances) {
      return;
    }

    const missingInstances = minInstances - fields.length;
    Array.from({ length: missingInstances }).forEach(() => append({}));
  }, [append, fields.length, minInstances]);

  const canAddMore =
    !config.maxInstances || fields.length < config.maxInstances;

  const prefixedSections = useMemo(
    () =>
      fields.map((_, index) =>
        prefixFormSections(config.sections, `${config.id}.${index}`),
      ),
    [config.id, config.sections, fields],
  );

  return (
    <YStack gap="$5">
      {fields.map((field, index) => (
        <YStack key={field.id} gap="$true">
          {fields.length > 1 && (
            <XStack justifyContent="space-between" alignItems="center">
              <SizableText size="$h6" fontFamily="$bold">
                {`${config.title} #${index + 1}`}
              </SizableText>

              {fields.length > minInstances && (
                <Button
                  variant="outlined-destructive"
                  icon={
                    <Ionicons
                      name="trash-outline"
                      color={theme.$error?.val}
                      size={16}
                    />
                  }
                  onPress={() => remove(index)}
                />
              )}
            </XStack>
          )}

          <FormSectionList sections={prefixedSections[index]} />

          {index < fields.length - 1 && (
            <Separator backgroundColor="$borderColor" />
          )}
        </YStack>
      ))}

      {canAddMore && (
        <YStack gap="$1">
          <FieldTitle label={t(config.addButtonSectionTitle)} />
          <Button
            variant="outlined"
            title={config.addButtonLabel}
            onPress={() => append({})}
          />
        </YStack>
      )}
    </YStack>
  );
};
